'use client'

import { ChevronDown16, ChevronUp16 } from '@vinted/monochrome-icons'
import { Accordion, Icon } from '@vinted/web-ui'
import { useMemo, useState } from 'react'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

type AccordionItemModel = { title: string; content: string; key: string }

const ACCORDION_ITEM_COUNT = 4

const generateAccordionContent = (): Array<AccordionItemModel> =>
  Array.from({ length: ACCORDION_ITEM_COUNT }, (_, index) => index + 1).map(order => ({
    key: String(order),
    title: `item${order}.title`,
    content: `item${order}.content`,
  }))

const renderIcon = ({ isExpanded }: { isExpanded: boolean }) => (
  <Icon
    name={isExpanded ? ChevronUp16 : ChevronDown16}
    display={Icon.Display.Block}
    color={Icon.Color.GreyscaleLevel1}
  />
)

const AccordionSection = () => {
  const translate = useTranslate('business.pro_integrations.accordion')
  const isProIntegrationsApplicationEnabled = useFeatureSwitch(
    'pro_integrations_application_page_enabled',
  )

  const [isExpandedMap, setIsExpandedMap] = useState<Record<string, boolean>>({})

  const handleToggle = (key: string) => (isExpanded: boolean) => {
    setIsExpandedMap(prev => ({
      ...prev,
      [key]: isExpanded,
    }))
  }

  const generatedContent = useMemo(() => {
    const result = generateAccordionContent()
    if (isProIntegrationsApplicationEnabled) {
      result[0]!.title = 'item1_application_form.title'
      result[0]!.content = 'item1_application_form.content'
    }

    return result
  }, [isProIntegrationsApplicationEnabled])

  const renderAccordion = (accordionContent: Array<AccordionItemModel>) =>
    accordionContent.map(({ title, content, key }) => (
      <div className="pro-integrations__accordion-group-item" key={key}>
        <Accordion
          // eslint-disable-next-line react/no-danger
          title={<div dangerouslySetInnerHTML={{ __html: translate(title) }} />}
          /* eslint-disable-next-line react/no-danger */
          content={<div dangerouslySetInnerHTML={{ __html: translate(content) }} />}
          closedSuffix={renderIcon({ isExpanded: false })}
          expandedSuffix={renderIcon({ isExpanded: true })}
          headerId="accordion_pro-integration-header"
          bodyId="accordion_pro-integration-body"
          testId={`pro-integrations-accordion-item${key}`}
          isExpanded={isExpandedMap[key] || false}
          onToggle={handleToggle(key)}
        />
      </div>
    ))

  return (
    <section className="pro-integrations__accordion" data-testid="pro-integrations-accordion">
      <h2
        className="pro-integrations__accordion-title"
        data-testid="pro-integrations-accordion-title"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: translate('title') }}
      />
      <div className="pro-integrations__accordion-group">{renderAccordion(generatedContent)}</div>
    </section>
  )
}

export default AccordionSection
